import Close from '@mui/icons-material/Close';
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { Fragment, ReactNode, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useGetDicListQuery } from '@/api/common';
import { useMergeFamilyRelationshipMutation } from '@/api/family-management/familyApi';
import { FamilyMemberRelationshipDTO } from '@/api/DTO/member-management/admissionRequest';

export interface FamilyPairPerson {
	nameEn?: string;
	nameZh?: string;
	fileNo?: string;
	familyMemberFileNo?: string;
	id?: string;
	memberIdDesc?: string | null;
	familyMemberIdDesc?: string | null;
}

interface Props {
	open: boolean;
	onClose: () => void;
	onSuccess: () => void;
	title: string;
	successMessage: string;
	/** Rows of the merge-combination list, in the order returned. */
	pairs: FamilyMemberRelationshipDTO[];
	/** The member whose Manage family member page is open (source of the first pair). */
	member: FamilyPairPerson;
	/** The person being added is already in another family group. */
	isMerge: boolean;
	/** Shown above the pairs, e.g. the added person's information. */
	children?: ReactNode;
}

interface PairForm {
	pairs: {
		relationshipToMember: string;
		relationshipToAddedPerson: string;
	}[];
}

const emptyPair = { relationshipToMember: '', relationshipToAddedPerson: '' };

const FamilyPairRelationshipDialog = ({
	open,
	onClose,
	onSuccess,
	title,
	successMessage,
	pairs,
	member,
	isMerge,
	children,
}: Props) => {
	const { t } = useTranslation();
	const [mergeFamilyRelationship, { isLoading }] =
		useMergeFamilyRelationshipMutation();

	const { data: relationshipDicList } = useGetDicListQuery('relationship', {
		refetchOnMountOrArgChange: true,
	});
	const relationshipOptions = (relationshipDicList?.data || []).map(
		(item: any) => ({ ...item, name: item.labelEn })
	);

	const formSchema = Yup.object().shape({
		pairs: Yup.array().of(
			Yup.object().shape({
				relationshipToMember: Yup.string().required(
					t('errorMessage.pleaseSelect')
				),
				relationshipToAddedPerson: Yup.string().required(
					t('errorMessage.pleaseSelect')
				),
			})
		),
	});

	const methods = useForm<PairForm>({
		defaultValues: { pairs: [] },
		resolver: yupResolver(formSchema) as any,
	});

	useEffect(() => {
		if (open) methods.reset({ pairs: pairs.map(() => ({ ...emptyPair })) });
	}, [open, pairs]);

	// Sources are never returned with display data. The first pair's source is
	// the page's member; everyone else appears as a target in some pair.
	const people = useMemo(() => {
		const map = new Map<string, FamilyPairPerson>();
		pairs.forEach((pair) => {
			if (pair.targetMemberId)
				map.set(pair.targetMemberId, {
					nameEn: pair.targetMemberNameEn,
					nameZh: pair.targetMemberNameZh,
					fileNo: pair.targetMemberFileNo,
					familyMemberFileNo: pair.targetFamilyMemberFileNo,
					memberIdDesc: pair.targetMemberIdDesc,
					familyMemberIdDesc: pair.targetFamilyMemberIdDesc,
				});
		});
		if (pairs[0]?.sourceMemberId)
			map.set(pairs[0].sourceMemberId, { ...member, memberIdDesc: member.id });
		return map;
	}, [pairs, member]);

	// The joining side (B and B's group) is every target of the page member's pairs.
	const joiningIds = useMemo(() => {
		const memberId = pairs[0]?.sourceMemberId;
		return pairs
			.filter((pair) => pair.sourceMemberId === memberId)
			.map((pair) => pair.targetMemberId || '');
	}, [pairs]);

	// (B, Ai) rows are shown as (Ai, B) so Person 1 is always from this family and
	// Person 2 always from the joining side.
	const rows = useMemo(
		() =>
			pairs.map((pair, index) => {
				const swapped = joiningIds.includes(pair.sourceMemberId || '');
				return {
					index,
					personOneId: swapped ? pair.targetMemberId : pair.sourceMemberId,
					personTwoId: swapped ? pair.sourceMemberId : pair.targetMemberId,
					// relationshipToMember = source's relationship to target.
					personOneField: swapped
						? ('relationshipToAddedPerson' as const)
						: ('relationshipToMember' as const),
					personTwoField: swapped
						? ('relationshipToMember' as const)
						: ('relationshipToAddedPerson' as const),
				};
			}),
		[pairs, joiningIds]
	);

	// This family is the page member plus everyone paired with the added person
	// as source; the joining family is the added person and their group.
	const mergeGroups = useMemo(() => {
		const memberId = pairs[0]?.sourceMemberId;
		const addedId = pairs[0]?.targetMemberId;
		const names = (ids: (string | undefined)[]) =>
			ids
				.map((id) => (id && people.get(id)) || {})
				.map((person) =>
					[person.nameEn, person.nameZh].filter(Boolean).join(' ')
				);
		return {
			thisFamily: names([
				memberId,
				...pairs
					.filter((pair) => pair.sourceMemberId === addedId)
					.map((pair) => pair.targetMemberId),
			]),
			joiningFamily: names(joiningIds),
		};
	}, [pairs, joiningIds, people]);

	const renderPerson = (id?: string) => {
		const person = (id && people.get(id)) || {};
		// Same links as the Manage family member list.
		const fileNos = [
			{
				fileNo: person.fileNo,
				href:
					person.memberIdDesc &&
					`/member-management/member-list/detail/${person.memberIdDesc}`,
			},
			{
				fileNo: person.familyMemberFileNo,
				href:
					person.familyMemberIdDesc &&
					`/family-management/family-management-list/detail/${person.familyMemberIdDesc}`,
			},
		].filter((item) => item.fileNo);
		return (
			<Box>
				<Typography variant='body2'>{person.nameEn || '-'}</Typography>
				<Typography variant='body2'>{person.nameZh}</Typography>
				{fileNos.length > 0 && (
					<Typography variant='caption' color='common.neutral40'>
						{fileNos.map((item, index) => (
							<Fragment key={index}>
								{index > 0 && ' / '}
								{item.href ? (
									<a href={item.href} target='_blank' rel='noopener noreferrer'>
										{item.fileNo}
									</a>
								) : (
									item.fileNo
								)}
							</Fragment>
						))}
					</Typography>
				)}
			</Box>
		);
	};

	const onSubmit = async (data: PairForm) => {
		const response = await mergeFamilyRelationship({
			relationshipList: pairs.map((pair, index) => ({
				sourceMemberType: pair.sourceMemberType ?? 0,
				sourceMemberId: pair.sourceMemberId || '',
				targetMemberType: pair.targetMemberType ?? 0,
				targetMemberId: pair.targetMemberId || '',
				relationshipToMember: data.pairs[index].relationshipToMember,
				relationshipToAddedPerson: data.pairs[index].relationshipToAddedPerson,
			})),
		});
		if ('data' in response) {
			enqueueSnackbar(successMessage, {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => <Close onClick={() => closeSnackbar(key)} />,
			});
			onSuccess();
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth='xl'
			fullWidth
			onKeyDown={(e) => {
				if (e.key === 'Tab') {
					e.stopPropagation();
				}
			}}
		>
			<DialogTitle variant='h5'>{title}</DialogTitle>
			<IconButton
				aria-label='close'
				onClick={onClose}
				sx={{
					position: 'absolute',
					right: 8,
					top: 8,
					color: (theme) => theme.palette.grey[500],
				}}
			>
				<Close />
			</IconButton>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<DialogContent>
						{children}
						{isMerge && (
							<Alert
								severity='warning'
								sx={{
									marginBottom: '12px',
									'& .MuiAlert-message': { flex: 1 },
								}}
							>
								{t('family.mergeFamilyGroupHint')}
								<Box display='flex' flexWrap='wrap' gap={2} marginTop={1.5}>
									{[
										{
											label: t('family.thisFamily'),
											names: mergeGroups.thisFamily,
										},
										{
											label: t('family.joiningFamily'),
											names: mergeGroups.joiningFamily,
										},
									].map((group) => (
										<Box
											key={group.label}
											sx={{
												flex: '1 1 240px',
												// Semi-transparent white: a lighter shade of the alert's own orange.
												bgcolor: 'rgba(255, 255, 255, 0.5)',
												borderRadius: 1,
												paddingX: 2,
												paddingY: 1.5,
											}}
										>
											<Typography variant='body2' fontWeight={500}>
												{group.label} ({group.names.length})
											</Typography>
											<Box
												component='ul'
												sx={{ margin: '4px 0 0', paddingLeft: '20px' }}
											>
												{group.names.map((name, index) => (
													<Typography
														component='li'
														variant='body2'
														key={index}
													>
														{name}
													</Typography>
												))}
											</Box>
										</Box>
									))}
								</Box>
							</Alert>
						)}
						<Typography variant='body1' sx={{ marginBottom: '4px' }}>
							{t('family.relationshipPairs')}
						</Typography>
						<Typography variant='body2' sx={{ marginBottom: '12px' }}>
							{t('family.relationshipPairsText')}
						</Typography>
						<TableContainer sx={{ maxHeight: '60vh' }}>
							<Table size='small' stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell width={40}>#</TableCell>
										<TableCell>
											{isMerge
												? `${t('family.personOne')} (${t('family.thisFamily')})`
												: t('family.personOne')}
										</TableCell>
										<TableCell>
											{isMerge
												? `${t('family.personTwo')} (${t('family.joiningFamily')})`
												: t('family.personTwo')}
										</TableCell>
										<TableCell>
											{t('family.relationshipPersonOneToTwo')}
										</TableCell>
										<TableCell>
											{t('family.relationshipPersonTwoToOne')}
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{rows.map((row) => (
										<TableRow key={`${row.personOneId}-${row.personTwoId}`}>
											<TableCell sx={{ verticalAlign: 'top', paddingTop: 2 }}>
												{row.index + 1}
											</TableCell>
											<TableCell sx={{ verticalAlign: 'top', paddingTop: 2 }}>
												{renderPerson(row.personOneId)}
											</TableCell>
											<TableCell sx={{ verticalAlign: 'top', paddingTop: 2 }}>
												{renderPerson(row.personTwoId)}
											</TableCell>
											<TableCell sx={{ minWidth: 220, paddingY: 1.5 }}>
												<AppDropDownMenu
													control={methods.control}
													name={`pairs.${row.index}.${row.personOneField}`}
													label={t('preAdmission.relationship') + '*'}
													options={relationshipOptions}
												/>
											</TableCell>
											<TableCell sx={{ minWidth: 220, paddingY: 1.5 }}>
												<AppDropDownMenu
													control={methods.control}
													name={`pairs.${row.index}.${row.personTwoField}`}
													label={t('preAdmission.relationship') + '*'}
													options={relationshipOptions}
												/>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</DialogContent>
					<DialogActions>
						<Button sx={{ textTransform: 'none' }} onClick={onClose}>
							{t('button.back')}
						</Button>
						<Button type='submit' variant='contained' disabled={isLoading}>
							{isMerge ? t('family.confirmAndMerge') : t('button.confirm')}
						</Button>
					</DialogActions>
				</form>
			</FormProvider>
		</Dialog>
	);
};

export default FamilyPairRelationshipDialog;
